// src/hooks/useSisterIndex.ts
// ---------------------------
// db-205: per-service "sister services" map for LIST screens (DFV Services list,
// telemarketing call list). Loads the active memberships + groups once per mount
// (one small query each — never one per row) and builds the index against the
// shelters the screen already has. Optional `reloadKey`: when it changes, the
// data is loaded again (the DFV Services list passes the open service code, so
// coming BACK from a service record picks up any sister-group edit made there —
// the list stays mounted while the record is open). FAIL-SAFE: any error / null result gives an
// EMPTY map, so a list simply shows no sisters and keeps working.

import { useEffect, useMemo, useState } from "react";
import { listActiveSisterGroups, listActiveSisterMembers } from "../data/sisterGroups";
import {
  buildSisterIndex,
  type SisterEntry,
  type SisterGroup,
  type SisterMember,
  type SisterShelter,
} from "../services/sisterGroups";

type Loaded = { members: SisterMember[]; groups: SisterGroup[] } | null;

export function useSisterIndex(
  shelters: SisterShelter[] | null | undefined,
  reloadKey?: unknown
): Map<string, SisterEntry> {
  const [loaded, setLoaded] = useState<Loaded>(null);

  // reloadKey is a deliberate re-load trigger (not read inside the effect).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [members, groups] = await Promise.all([
          listActiveSisterMembers(),
          listActiveSisterGroups(),
        ]);
        if (cancelled || !members || !groups) return;
        setLoaded({ members, groups });
      } catch {
        // fail-safe: leave the index empty
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return useMemo(
    () =>
      loaded
        ? buildSisterIndex(loaded.members, loaded.groups, shelters)
        : new Map<string, SisterEntry>(),
    [loaded, shelters]
  );
}
